import { CardFields, EMPTY_CARD, PaymentMethodSelector, validateCard } from '@/ds'
import type { CardErrors, CardValue, PaymentMethod } from '@/ds'
import type { CardInput, PaymentSummary } from '@/services/pagos'

export interface CardChoiceValue {
  methodId: string
  card: CardValue
}

/** Método inicial: la tarjeta guardada si existe (como `useValidCardIdIfExists` de la app actual). */
export function initialChoice(summary: PaymentSummary): CardChoiceValue {
  const saved = summary.methods.find((m) => m.kind === 'saved_card')
  return { methodId: saved?.id ?? summary.methods.find((m) => m.kind === 'card')?.id ?? 'card', card: EMPTY_CARD }
}

const isSaved = (summary: PaymentSummary, choice: CardChoiceValue) => summary.savedCard !== null && summary.methods.some((m) => m.kind === 'saved_card' && m.id === choice.methodId)

export function choiceErrors(summary: PaymentSummary, choice: CardChoiceValue): CardErrors {
  if (isSaved(summary, choice)) return summary.savedCard?.needsCvv ? validateCard(choice.card, 'cvv') : {}
  return validateCard(choice.card, 'new')
}

/** Tarjeta para `pagos.pay`: la guardada (con CVV solo si el PMS lo pide) o los datos de la nueva. */
export function toCardInput(summary: PaymentSummary, choice: CardChoiceValue): CardInput {
  if (isSaved(summary, choice) && summary.savedCard) return { kind: 'saved', id: summary.savedCard.id, cvv: summary.savedCard.needsCvv ? choice.card.cvv : null }
  return { kind: 'new', name: choice.card.name, number: choice.card.number, expiry: choice.card.expiry, cvv: choice.card.cvv }
}

/** Últimas cifras de la tarjeta elegida, si se conocen (para "tarjeta acabada en 4421"). */
export function chosenLast4(summary: PaymentSummary, choice: CardChoiceValue): string | null {
  if (isSaved(summary, choice)) return summary.savedCard?.last4 ?? null
  const digits = choice.card.number.replace(/\D/g, '')
  return digits.length >= 4 ? digits.slice(-4) : null
}

/** Selector de método (D10: tarjeta guardada y otra tarjeta) y los campos que pide el método elegido. */
export function CardChoice({ summary, value, onChange, showErrors, disabled }: { summary: PaymentSummary; value: CardChoiceValue; onChange: (v: CardChoiceValue) => void; showErrors: boolean; disabled?: boolean }) {
  const methods: PaymentMethod[] = summary.methods.flatMap((m): PaymentMethod[] => {
    if (m.kind === 'saved_card') return [{ id: m.id, kind: 'saved_card', ...(summary.savedCard?.last4 ? { detail: `···· ${summary.savedCard.last4}` } : {}) }]
    if (m.kind === 'card') return [{ id: m.id, kind: 'card' }]
    return []
  })
  const saved = isSaved(summary, value)
  const errors = showErrors ? choiceErrors(summary, value) : {}
  return (
    <div className="pagos__group">
      <PaymentMethodSelector methods={methods} value={value.methodId} onChange={(methodId) => onChange({ methodId, card: EMPTY_CARD })} />
      {saved && summary.savedCard?.needsCvv && (
        <CardFields mode="cvv" value={value.card} onChange={(card) => onChange({ ...value, card })} errors={errors} disabled={disabled ?? false} />
      )}
      {!saved && <CardFields mode="new" value={value.card} onChange={(card) => onChange({ ...value, card })} errors={errors} disabled={disabled ?? false} />}
    </div>
  )
}
